import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, UploadSimple, DownloadSimple, Trash, FilePdf, FileImage, File as FileIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/common";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.xlsx,.xls,.docx,.doc";
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const iconOf = (t) => (t === "application/pdf" ? FilePdf : t.startsWith("image/") ? FileImage : FileIcon);

export async function uploadFile(entityType, entityId, file) {
  const fd = new FormData();
  fd.append("entity_type", entityType);
  fd.append("entity_id", entityId);
  fd.append("file", file);
  return (await api.post("/files", fd)).data;
}

export async function openFile(f) {
  const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  if (f.content_type?.startsWith("image/") || f.content_type === "application/pdf") {
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = f.original_filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function Attachments({ entityType, entityId, title = "Dosya Ekleri" }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [files, reload] = useLoad(() => api.get("/files", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => r.data), [entityType, entityId]);

  const onPick = async (e) => {
    const picked = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    for (const f of picked) {
      try {
        await uploadFile(entityType, entityId, f);
        toast.success(`${f.name} yüklendi`);
      } catch (err) {
        toast.error(`${f.name}: ${errMsg(err)}`);
      }
    }
    setBusy(false);
    reload();
  };
  const del = async (f) => {
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("Dosya kaldırıldı");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="panel no-print" data-testid={`attachments-${entityType}`}>
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold"><Paperclip size={18} className="text-teal-700" /> {title}</h3>
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={onPick} data-testid="attachments-file-input" />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} className="rounded-[4px] border-teal-600 text-teal-700" data-testid="attachments-upload-button">
          <UploadSimple /> {busy ? "Yükleniyor…" : "Dosya Ekle"}
        </Button>
      </div>
      <ul className="divide-y divide-border">
        {files && files.length === 0 && <li className="px-5 py-6 text-center text-sm text-muted-foreground" data-testid="attachments-empty">Henüz dosya eklenmedi. PDF, resim, Excel veya Word (en fazla 15 MB).</li>}
        {files?.map((f) => {
          const Icon = iconOf(f.content_type);
          return (
            <li key={f.id} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`attachment-row-${f.id}`}>
              <Icon size={22} className="shrink-0 text-teal-700" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium" title={f.original_filename}>{f.label ? <span className="mr-1 rounded bg-orange-100 px-1 text-[10px] font-bold text-orange-800">{f.label}</span> : null}{f.original_filename}</div>
                <div className="text-[11px] text-muted-foreground">{size(f.size)} · {fmtDateTime(f.created_at)} · {f.uploaded_by}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => openFile(f).catch((e) => toast.error(errMsg(e)))} data-testid={`attachment-download-${f.id}`}><DownloadSimple /></Button>
              <Confirm title="Dosya kaldırılsın mı?" description={f.original_filename} onConfirm={() => del(f)} testid={`attachment-delete-confirm-${f.id}`}
                trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`attachment-delete-${f.id}`}><Trash /></Button>} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
